import { useRef, useState } from "react";
import { Camera, ImagePlus, X, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, photoSrc } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Spinner } from "@/components/Shared";

const OK_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

export default function PhotoUploader({ ensureReport, photos, setPhotos, onPendingChange, maxMb = 15, disabled }) {
  const [queue, setQueue] = useState([]);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const patch = (key, upd) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...upd } : x)));
  const pendingDelta = (n) => onPendingChange?.((p) => Math.max(0, p + n));

  const upload = async (rid, item) => {
    patch(item.key, { status: "uploading", progress: 0, error: "" });
    const fd = new FormData();
    fd.append("file", item.file, item.file.name);
    try {
      const { data } = await api.post(`/reports/${rid}/photos`, fd, {
        onUploadProgress: (e) => e.total && patch(item.key, { progress: Math.round((e.loaded / e.total) * 100) }),
      });
      setPhotos((p) => [...p, data]);
      setQueue((q) => q.filter((x) => x.key !== item.key));
      URL.revokeObjectURL(item.preview);
      pendingDelta(-1);
    } catch (e) {
      patch(item.key, { status: "error", error: errMsg(e, "Upload failed") });
      pendingDelta(-1);
    }
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const rid = await ensureReport();
    if (!rid) return;
    for (const f of files) {
      const type = (f.type || "").toLowerCase();
      if (type && !OK_TYPES.includes(type)) { toast.error(`${f.name}: only photos (JPG, PNG, WEBP, HEIC) are allowed`); continue; }
      const file = await compressImage(f);
      if (file.size > maxMb * 1024 * 1024) { toast.error(`${f.name} is larger than ${maxMb} MB`); continue; }
      const item = { key: `${Date.now()}-${Math.random()}`, file, preview: URL.createObjectURL(file), progress: 0, status: "uploading" };
      setQueue((q) => [...q, item]);
      pendingDelta(1);
      await upload(rid, item);
    }
  };

  const retry = async (item) => { const rid = await ensureReport(); if (rid) { pendingDelta(1); upload(rid, item); } };
  const discard = (item) => { setQueue((q) => q.filter((x) => x.key !== item.key)); if (item.status === "uploading") pendingDelta(-1); };
  const removePhoto = async (p) => {
    try {
      await api.delete(`/photos/${p.id}`);
      setPhotos((list) => list.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveCaption = async (p, caption) => {
    if ((p.caption || "") === caption.trim()) return;
    try {
      const { data } = await api.put(`/photos/${p.id}`, { caption });
      setPhotos((list) => list.map((x) => (x.id === p.id ? { ...x, caption: data.caption } : x)));
    } catch (e) {
      toast.error(errMsg(e, "Caption not saved"));
    }
  };

  const onPick = (e) => { addFiles(e.target.files); e.target.value = ""; };

  return (
    <div className="space-y-3" data-testid="photo-uploader">
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={disabled} onClick={() => camRef.current?.click()} className="btn-brand h-14" data-testid="photo-take-button">
          <Camera className="h-5 w-5" />Take Photo
        </button>
        <button type="button" disabled={disabled} onClick={() => galRef.current?.click()} className="btn-ghost h-14" data-testid="photo-gallery-button">
          <ImagePlus className="h-5 w-5" />From Gallery
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="photo-camera-input" />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="photo-file-input" />
      {disabled && <p className="text-xs text-slate-500">Select a client, project and date to add photos.</p>}
      {(photos.length > 0 || queue.length > 0) && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="photo-thumbnails">
          {photos.map((p) => (
            <div key={p.id} className="space-y-1" data-testid={`photo-thumb-${p.id}`}>
              <div className="relative aspect-square overflow-hidden rounded-md border border-border bg-slate-100">
                <img src={photoSrc(p.id)} alt={p.caption || p.filename} className="h-full w-full object-cover" loading="lazy" />
                <button type="button" onClick={() => removePhoto(p)} className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white" data-testid={`photo-remove-${p.id}`} aria-label="Remove photo">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <input defaultValue={p.caption || ""} maxLength={200} placeholder="Add note…" onBlur={(e) => saveCaption(p, e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                className="h-9 w-full rounded border border-input bg-white px-2 text-xs focus:border-[#5C241E] focus:outline-none" data-testid={`photo-caption-input-${p.id}`} aria-label="Photo caption" />
            </div>
          ))}
          {queue.map((q) => (
            <div key={q.key} className="relative aspect-square self-start overflow-hidden rounded-md border border-border bg-slate-100" data-testid="photo-uploading-item">
              {q.status !== "error" && <img src={q.preview} alt="" className="h-full w-full object-cover opacity-60" />}
              {q.status === "uploading" ? (
                <div className="absolute inset-x-0 bottom-0 bg-black/60 p-1.5">
                  <div className="h-1.5 overflow-hidden rounded bg-white/30"><div className="h-full bg-white transition-[width]" style={{ width: `${q.progress}%` }} /></div>
                  <p className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-white"><Spinner className="h-3 w-3 text-white" />{q.progress}%</p>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/60 p-1 text-center text-white">
                  <AlertTriangle className="h-4 w-4 text-amber-300" />
                  <p className="text-[10px] leading-tight">{q.error}</p>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => retry(q)} className="rounded bg-white px-2 py-1 text-[10px] font-bold text-slate-900" data-testid="photo-retry-button"><RotateCcw className="inline h-3 w-3" /> Retry</button>
                    <button type="button" onClick={() => discard(q)} className="rounded bg-white/20 px-2 py-1 text-[10px] font-bold" data-testid="photo-discard-button">Remove</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-600" data-testid="photo-count">{photos.length} photo{photos.length === 1 ? "" : "s"} attached{queue.some((q) => q.status === "uploading") ? ` · ${queue.filter((q) => q.status === "uploading").length} uploading` : ""}{queue.some((q) => q.status === "error") ? ` · ${queue.filter((q) => q.status === "error").length} failed (not attached)` : ""}</p>
    </div>
  );
}
